// Shared types mirroring the backend schemas
export interface ExtractedTable {
  headers: string[];
  rows: string[][];
}

export interface ExtractedPage {
  title: string;
  tables: ExtractedTable[];
  text: string;
  tablesAsCsv: string[];
}

export interface Notebook {
  id: string;
  displayName: string;
  userRole?: string;
}

export interface Section {
  id: string;
  displayName: string;
}

export interface Page {
  id: string;
  title: string;
  lastModifiedDateTime?: string;
}

export interface Site {
  id: string;
  displayName?: string;
  webUrl?: string;
}

export type Provider = "openai" | "anthropic" | "fireworks";

export interface ProviderInfo {
  label: string;
  description: string;
  defaultModel: string;
  models: string[];
}

export interface HealthResponse {
  status: string;
  authenticated: boolean;
  username?: string;
  authMode: "dev" | "cognito";
  role?: "admin" | "user" | "unconfirmed" | null;
  groups?: string[];
  providers: {
    available: Provider[];
    info: Record<Provider, ProviderInfo>;
  };
  wsEndpoint: string | null;
}

export interface AdminUser {
  username: string;
  sub?: string;
  email?: string;
  status: string;
  groups: string[];
  created: string;
}

export type ThinkingEffort = "low" | "medium" | "high";

export interface ReasoningStep {
  step: number;
  summaries: string[];
}

export interface ToolCallRecord {
  name: string;
  arguments: unknown;
  output?: unknown;
  error?: string;
}

export interface WebCitation {
  url: string;
  title?: string;
}

export interface TokenUsage {
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  reasoningTokens: number;
}

export interface ChatResponse {
  sessionId: string;
  sessionTitle: string;
  provider: Provider;
  text: string;
  toolCalls: ToolCallRecord[];
  webCitations: WebCitation[];
  reasoning: ReasoningStep[];
  usage?: TokenUsage;
}

/** Structured error payload from the chat-worker. Matches the
 *  error-classify.ts shape: stable `kind`, plain-English `userMessage`,
 *  optional upstream `providerTraceId`, and an 8-char `errorId` for
 *  CloudWatch correlation. */
export interface ChatErrorPayload {
  kind: string;
  userMessage: string;
  providerTraceId: string | null;
  errorId: string | null;
  raw: string;
}

export type StreamChunk =
  | { type: "text-start"; id: string }
  | { type: "text-delta"; id: string; text: string }
  | { type: "text-end"; id: string }
  | { type: "reasoning-start"; id: string }
  | { type: "reasoning-delta"; id: string; text: string }
  | { type: "reasoning-end"; id: string }
  | { type: "tool-call"; toolCallId: string; toolName: string; args?: unknown }
  | { type: "tool-call-delta"; argsTextDelta: string; toolCallId: string }
  | { type: "tool-result"; toolCallId: string; toolName: string; result: unknown }
  | { type: "tool-error"; toolCallId: string; toolName: string; error: unknown }
  | { type: "source"; id: string; sourceType: string; title: string; url?: string }
  | { type: "step-finish"; output: { usage?: TokenUsage } }
  | { type: "finish"; output: { usage?: TokenUsage } }
  | { type: "error"; error: ChatErrorPayload | string }
  | { type: "done"; sessionId: string };

// ---- Sessions ----

export interface SessionMeta {
  id: string;
  title: string;
  provider: Provider;
  model: string;
  thinkingEffort: ThinkingEffort;
  sectionId: string;
  sectionName: string;
  notebookId: string;
  notebookName: string;
  siteId: string;
  createdAt: string;
  updatedAt: string;
}

export interface SessionListItem extends SessionMeta {
  msgCount: number;
  lastUserMsgPreview: string | null;
}

export interface ChatMessageRecord {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
  provider?: Provider;
  model?: string;
  openaiResponseId?: string;
  reasoning?: ReasoningStep[];
  toolCalls?: ToolCallRecord[];
  webCitations?: WebCitation[];
  sectionScope?: string;
  usage?: TokenUsage;
}

export interface SessionDetail extends SessionMeta {
  messages: ChatMessageRecord[];
}

// ---- Corpus / sync types ----

export type ScopeType = "site" | "notebook" | "section" | "page";

export interface SyncScope {
  type: ScopeType;
  id: string;
  siteId: string;
  label?: string;
  notebookId?: string;
  notebookName?: string;
  sectionId?: string;
  sectionName?: string;
}

export interface PageStatus {
  pageId: string;
  syncedAt: string;
  graphModifiedAt: string;
  status: string;
  webUrl: string | null;
  notebookName: string;
  sectionName: string;
  title: string;
  remoteModifiedAt: string | null;
  isStale: boolean;
}

export interface SearchHit {
  pageId: string;
  title: string;
  notebook: string;
  section: string;
  snippet: string;
  attachmentsSnippet: string;
  rank: number;
  webUrl: string | null;
  syncedAt: string;
}

export interface AutoSyncRule {
  scopeType: string;
  scopeId: string;
  scopeLabel: string | null;
  intervalMinutes: number;
  enabled: boolean;
  lastRunAt: string | null;
}

export interface AttachmentDetail {
  resourceId: string;
  filename: string;
  kind: string;
  status: string;
  extractedAt: string | null;
  error: string | null;
  bytesPath: string;
  textPath: string | null;
  textPreview: string | null;
}

export interface PageDetail {
  pageId: string;
  title: string;
  notebook: string;
  section: string;
  notebookId: string;
  sectionId: string;
  siteId: string;
  webUrl: string | null;
  syncedAt: string;
  graphModifiedAt: string;
  status: string;
  isStale: boolean;
  mdPath: string;
  markdown: string;
  metaJson: string | null;
  attachments: AttachmentDetail[];
  /** Error fields — populated only when status === "error". */
  errorId: string | null;
  errorKind: string | null;
  errorMessage: string | null;
  errorProviderTraceId: string | null;
  errorAt: string | null;
}

/** Sync History — one archived job (terminal snapshot, 1-year TTL). */
export interface SyncJobArchive {
  jobId: string;
  userSub: string;
  startedAt: string;
  completedAt: string;
  durationMs: number;
  scopeType: string;
  scopeId: string;
  scopeLabel: string | null;
  siteId: string;
  pagesTotal: number;
  pagesDone: number;
  pagesErrored: number;
  status: "completed" | "partial" | "failed" | "cancelled";
  triggerKind: "manual" | "auto-sync" | "force-resync" | "retry";
  topErrorKind: string | null;
  topErrorKindCount: number;
  ttlExpiresAt: number;
}

/** Sync History — one row per failed page within an archived job. */
export interface SyncJobError {
  jobId: string;
  pageId: string;
  userSub: string;
  errorAt: string;
  errorId: string;
  errorKind: string;
  userMessage: string;
  providerTraceId: string | null;
  pageTitle: string;
  notebookId: string;
  notebookName: string;
  sectionId: string;
  sectionName: string;
  stage: string;
  /** OneNote web URL (oneNoteWebUrl.href). Captured at error time, with
   *  legacy-row fallback to corpus_pages.web_url on the detail endpoint. */
  webUrl: string | null;
  ttlExpiresAt: number;
  /** Server annotation: true if the live corpus_pages row shows this
   *  page has been re-synced after this job's completedAt. */
  sinceFixed?: boolean;
}

export interface SectionInfo {
  sectionId: string;
  sectionName: string;
  notebookId: string;
  notebookName: string;
  siteId: string;
  pageCount: number;
}

/**
 * Live SSE event payloads — mirrors src/corpus/events.ts SyncEvent.
 */
export type SyncEvent =
  | { type: "job_started"; jobId: number; scopeType: string; scopeId: string; scopeLabel?: string }
  | { type: "page_started"; jobId: number; pageId: string; title: string }
  | { type: "page_done"; jobId: number; pageId: string; title: string; skipped: boolean }
  | { type: "page_error"; jobId: number; pageId: string; title: string; error: string }
  | {
      type: "attachment_started";
      jobId: number;
      pageId: string;
      resourceId: string;
      kind: string;
      filename: string;
      phase: "fetching";
    }
  | {
      type: "attachment_extracting";
      jobId: number;
      pageId: string;
      resourceId: string;
      kind: string;
      filename: string;
    }
  | {
      type: "attachment_done";
      jobId: number;
      pageId: string;
      resourceId: string;
      kind: string;
      filename: string;
      status: string;
      error?: string;
    }
  | {
      type: "job_progress";
      jobId: number;
      pagesDone: number;
      pagesTotal: number;
      attachmentsDone: number;
      attachmentsTotal: number;
    }
  | {
      type: "job_done";
      jobId: number;
      status: "ok" | "error" | "cancelled";
      pagesDone: number;
      pagesTotal: number;
      error?: string;
    };

async function doFetch(url: string, token: string | null, init?: RequestInit): Promise<Response> {
  return fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
}

async function j<T>(url: string, init?: RequestInit): Promise<T> {
  const { refreshIdToken } = await import("./auth.js");
  let token = localStorage.getItem("cognito_id_token");
  let res = await doFetch(url, token, init);
  if (res.status === 401 && token) {
    const refreshed = await refreshIdToken();
    if (refreshed) {
      token = refreshed;
      res = await doFetch(url, token, init);
    } else {
      window.location.href = "/";
      throw new Error("401 Unauthorized: session expired");
    }
  }
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  health: () => j<HealthResponse>("/api/health"),
  getSite: (hostname: string, sitePath?: string) => {
    const qs = new URLSearchParams({ hostname });
    if (sitePath) qs.set("sitePath", sitePath);
    return j<Site>(`/api/onenote/site?${qs}`);
  },
  listNotebooks: (siteId: string) =>
    j<{ notebooks: Notebook[] }>(
      `/api/onenote/notebooks?siteId=${encodeURIComponent(siteId)}`,
    ),
  listSections: (siteId: string, notebookId: string) =>
    j<{ sections: Section[] }>(
      `/api/onenote/sections?siteId=${encodeURIComponent(siteId)}&notebookId=${encodeURIComponent(notebookId)}`,
    ),
  listPages: (siteId: string, sectionId: string, nextLink?: string) => {
    // URLSearchParams URL-encodes values once on stringify — don't call
    // encodeURIComponent before passing in or the server sees double-
    // encoded values.
    const qs = new URLSearchParams({ siteId, sectionId });
    if (nextLink) qs.set("nextLink", nextLink);
    return j<{ pages: Page[]; nextLink: string | null }>(`/api/onenote/pages?${qs}`);
  },
  getPage: (siteId: string, pageId: string) =>
    j<ExtractedPage>(
      `/api/onenote/page?siteId=${encodeURIComponent(siteId)}&pageId=${encodeURIComponent(pageId)}`,
    ),
  chat: (opts: {
    message: string;
    sessionId?: string;
    provider?: Provider;
    model?: string;
    thinkingEffort?: ThinkingEffort;
    sectionId?: string;
    sectionName?: string;
    notebookId?: string;
    notebookName?: string;
    siteId?: string;
  }) =>
    j<ChatResponse>("/api/agent/chat", {
      method: "POST",
      body: JSON.stringify(opts),
    }),

  chatStream: (
    opts: {
      message: string;
      sessionId?: string;
      provider?: Provider;
      model?: string;
      thinkingEffort?: ThinkingEffort;
      sectionId?: string;
      sectionName?: string;
      notebookId?: string;
      notebookName?: string;
      siteId?: string;
    },
    onChunk: (chunk: StreamChunk) => void,
    signal?: AbortSignal,
  ): Promise<{ sessionId: string }> =>
    (async () => {
      const token = localStorage.getItem("cognito_id_token");
      const res = await fetch("/api/agent/chat", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(opts),
        signal,
      }) as Response;
      if (!res.ok) {
        const body = await res.text();
        throw new Error(`${res.status} ${res.statusText}: ${body}`);
      }
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let sessionId = opts.sessionId ?? "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          if (line.startsWith(":")) continue;
          if (!line.startsWith("data: ")) continue;
          const json = line.slice(6);
          if (!json.trim()) continue;
          try {
            const chunk = JSON.parse(json) as StreamChunk;
            onChunk(chunk);
            if (chunk.type === "done") sessionId = (chunk as { type: "done"; sessionId: string }).sessionId;
          } catch {
            // skip unparseable
          }
        }
      }
      return { sessionId };
    })(),
  // Sessions
  listSessions: () => j<{ sessions: SessionListItem[] }>("/api/sessions"),
  createSession: (opts: {
    provider: Provider;
    model: string;
    thinkingEffort?: ThinkingEffort;
    title?: string;
    sectionId: string;
    sectionName: string;
    notebookId: string;
    notebookName: string;
    siteId: string;
  }) =>
    j<SessionDetail>("/api/sessions", {
      method: "POST",
      body: JSON.stringify(opts),
    }),
  getSession: (id: string) =>
    j<SessionDetail>(`/api/sessions/${encodeURIComponent(id)}`),
  patchSession: (
    id: string,
    body: {
      title?: string;
      provider?: Provider;
      model?: string;
      thinkingEffort?: ThinkingEffort;
      sectionId?: string;
      sectionName?: string;
      notebookId?: string;
      notebookName?: string;
      siteId?: string;
    },
  ) =>
    j<SessionMeta>(`/api/sessions/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteSession: (id: string) =>
    j<{ ok: true }>(`/api/sessions/${encodeURIComponent(id)}`, {
      method: "DELETE",
    }),

  // ---- Corpus ----
  search: (q: string, opts: { limit?: number; notebook?: string; section?: string } = {}) => {
    const qs = new URLSearchParams({ q });
    if (opts.limit) qs.set("limit", String(opts.limit));
    if (opts.notebook) qs.set("notebook", opts.notebook);
    if (opts.section) qs.set("section", opts.section);
    return j<{ hits: SearchHit[] }>(`/api/search?${qs}`);
  },
  /**
   * Trigger a sync for a scope (page / section / notebook / site).
   *
   * Always sends `force=true` semantics (the lazy decision tree in the
   * raw-fetch worker handles unchanged pages — refreshRaw cache-hits HTML
   * with 0 Graph calls — and stageAttachment honors the staging cache when
   * the source bytes haven't moved). The route is named `force-resync`
   * for back-compat with the in-flight server handler; the *only* way to
   * trigger a sync is via this single button now.
   */
  sync: (scope: SyncScope) =>
    j<{
      jobId: string;
      status: "queued";
      /** Pages in the scope; named `purgeTotal` for legacy SPA hooks that
       * already read it. */
      purgeTotal: number;
    }>("/api/corpus/force-resync", {
      method: "POST",
      body: JSON.stringify({ scope }),
    }),
  chatSubmit: (opts: {
    message: string;
    sessionId?: string;
    provider?: Provider;
    model?: string;
    thinkingEffort?: ThinkingEffort;
    sectionId?: string;
    sectionName?: string;
    notebookId?: string;
    notebookName?: string;
    siteId?: string;
  }) =>
    j<{ jobId: string; sessionId: string }>("/api/agent/chat", {
      method: "POST",
      body: JSON.stringify(opts),
    }),
  jobProgress: (id: string) =>
    j<{
      jobId: string;
      userSub: string;
      scopeType: string;
      scopeId: string;
      scopeLabel: string | null;
      pagesTotal: number;
      pagesDone: number;
      pagesErrored: number;
      startedAt: string;
      lastUpdatedAt: string;
      status: "queued" | "running" | "completed" | "partial" | "failed" | "rate_limited";
      lastError: string | null;
      rateLimitedAt: string | null;
      phase: "purging" | "syncing";
      purgeTotal: number;
      purgeDone: number;
    }>(`/api/corpus/jobs/${encodeURIComponent(id)}`),
  listInFlightSyncs: () =>
    j<{
      jobs: Array<{
        jobId: string;
        userSub: string;
        scopeType: string;
        scopeId: string;
        scopeLabel: string | null;
        pagesTotal: number;
        pagesDone: number;
        pagesErrored: number;
        startedAt: string;
        lastUpdatedAt: string;
        status: "queued" | "running" | "rate_limited";
        lastError: string | null;
        rateLimitedAt: string | null;
        phase: "purging" | "syncing";
        purgeTotal: number;
        purgeDone: number;
      }>;
    }>("/api/corpus/sync/in-flight"),
  /** Errored pages within a job's scope. Drives the "Errors (N)"
   * expandable inside SyncProgressBanner. Sorted newest-first by
   * error_at. */
  jobErrors: (jobId: string) =>
    j<{
      errors: Array<{
        pageId: string;
        title: string;
        webUrl: string | null;
        notebookId: string;
        notebookName: string;
        sectionId: string;
        sectionName: string;
        siteId: string;
        errorId: string | null;
        errorKind: string | null;
        errorMessage: string | null;
        errorProviderTraceId: string | null;
        errorAt: string | null;
      }>;
      tip: string | null;
    }>(`/api/corpus/jobs/${encodeURIComponent(jobId)}/errors`),
  /** SQS queue depth across the three sync stages — used to display
   * "waiting in queue · ~N ahead" when a job is enqueued but not yet
   * picked up by a worker (common when a page-scope sync is enqueued
   * during an active section sync). */
  /** Sync History — list archived jobs newest-first. Cursor is the previous
   *  page's last startedAt. */
  listArchivedJobs: (cursor?: string | null, limit = 25) => {
    const qs = new URLSearchParams();
    if (cursor) qs.set("cursor", cursor);
    qs.set("limit", String(limit));
    return j<{ jobs: SyncJobArchive[]; nextCursor: string | null }>(
      `/api/corpus/sync/jobs?${qs}`,
    );
  },
  /** Single archived job + its errors + cluster tip + sinceFixed count. */
  getArchivedJob: (jobId: string) =>
    j<{
      job: SyncJobArchive;
      errors: SyncJobError[];
      tip: string | null;
      sinceFixedCount: number;
    }>(`/api/corpus/sync/jobs/${encodeURIComponent(jobId)}`),
  /** Substring search over archived job scope labels/IDs. */
  searchArchivedJobs: (q: string) => {
    const qs = new URLSearchParams({ q });
    return j<{ jobs: SyncJobArchive[]; nextCursor: string | null }>(
      `/api/corpus/sync/jobs/search?${qs}`,
    );
  },
  /** Re-enqueue all failed pages from an archived job as a new "retry" job. */
  retryArchivedJob: (jobId: string) =>
    j<{ jobId: string; pagesQueued: number }>(
      `/api/corpus/sync/jobs/${encodeURIComponent(jobId)}/retry`,
      { method: "POST" },
    ),
  syncQueueDepth: () =>
    j<{
      rawFetch: { queued: number; inFlight: number };
      stagingExtract: { queued: number; inFlight: number };
      finalStitch: { queued: number; inFlight: number };
    }>("/api/corpus/sync/queue-depth"),
  corpusStatus: (filter: {
    siteId?: string;
    notebookId?: string;
    sectionId?: string;
    pageIds?: string[];
  }) => {
    const qs = new URLSearchParams();
    if (filter.siteId) qs.set("siteId", filter.siteId);
    if (filter.notebookId) qs.set("notebookId", filter.notebookId);
    if (filter.sectionId) qs.set("sectionId", filter.sectionId);
    if (filter.pageIds?.length) qs.set("pageIds", filter.pageIds.join(","));
    return j<{ pages: PageStatus[] }>(`/api/corpus/status?${qs}`);
  },
  deleteCorpusPage: (pageId: string) =>
    j<{ ok: true }>(`/api/corpus/page/${encodeURIComponent(pageId)}`, {
      method: "DELETE",
    }),
  setAutoSync: (rule: {
    scopeType: ScopeType;
    scopeId: string;
    scopeLabel?: string;
    enabled: boolean;
    intervalMinutes: number;
  }) =>
    j<{ ok: true }>("/api/corpus/auto-sync", {
      method: "POST",
      body: JSON.stringify(rule),
    }),
  listAutoSync: () => j<{ rules: AutoSyncRule[] }>("/api/corpus/auto-sync"),
  getCorpusPage: (pageId: string) =>
    j<PageDetail>(`/api/corpus/page/${encodeURIComponent(pageId)}`),
  listCorpusSections: () =>
    j<{ sections: SectionInfo[] }>("/api/corpus/sections"),
  getAttachmentText: async (resourceId: string): Promise<string> => {
    const token = localStorage.getItem("cognito_id_token");
    const res = await fetch(
      `/api/corpus/attachment/${encodeURIComponent(resourceId)}/text`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} },
    );
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.text();
  },

  adminListUsers: () => j<{ users: AdminUser[] }>("/api/admin/users"),
  adminAddUserToGroup: (username: string, groupName: string) =>
    j<{ ok: true }>(`/api/admin/users/${encodeURIComponent(username)}/groups`, {
      method: "POST",
      body: JSON.stringify({ groupName }),
    }),
  adminRemoveUserFromGroup: (username: string, groupName: string) =>
    j<{ ok: true }>(`/api/admin/users/${encodeURIComponent(username)}/groups/${encodeURIComponent(groupName)}`, {
      method: "DELETE",
    }),
  adminDeleteUser: (username: string) =>
    j<{ ok: true }>(`/api/admin/users/${encodeURIComponent(username)}`, {
      method: "DELETE",
    }),
  adminSetUserRole: (username: string, role: "admin" | "user" | "unconfirmed") =>
    j<{ ok: true; role: string }>(
      `/api/admin/users/${encodeURIComponent(username)}/role`,
      {
        method: "POST",
        body: JSON.stringify({ role }),
      },
    ),

  microsoftStatus: () =>
    j<{ connected: boolean }>("/api/auth/microsoft/status"),

  microsoftStartDeviceCode: () =>
    j<{ userCode: string; verificationUri: string; expiresIn: number; interval: number }>("/api/auth/microsoft/device-code", {
      method: "POST",
    }),

  microsoftPollDeviceCode: () =>
    j<{ connected: boolean; pending?: boolean; expired?: boolean }>("/api/auth/microsoft/device-code/poll"),

  microsoftDisconnect: () =>
    j<{ ok: true }>("/api/auth/microsoft/disconnect", {
      method: "POST",
    }),

  microsoftGetConfig: () =>
    j<{
      clientId: string;
      tenantId: string;
      source: "user" | "global" | "unset";
    }>("/api/auth/microsoft/config"),

  microsoftSetConfig: (clientId: string, tenantId: string) =>
    j<{ clientId: string; tenantId: string; source: "user" }>(
      "/api/auth/microsoft/config",
      { method: "PUT", body: JSON.stringify({ clientId, tenantId }) },
    ),

  microsoftClearConfig: () =>
    j<{
      clientId: string;
      tenantId: string;
      source: "user" | "global" | "unset";
    }>("/api/auth/microsoft/config", { method: "DELETE" }),
};
